import { Box, Button, Theme, Typography } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import CommunicationTemplatesList from "./CommunicationTemplatesList";
import CustomDrawer from "../../common/CustomDrawer/CustomDrawer";
import { useState } from "react";
import CreateCommunicationTemplate from "./CreateCommunicationTemplate";
import {
  ICreatedTemplateData,
  IRevertTemplateVersionPayload,
} from "../../models/admin/communicationTemplates/communicatinTemplates";
import CustomDialog from "../../common/CustomDialog/CustomDialog";
import TemplateVersionsHistory from "./TemplateVersionsHistory";
import { setTemplateVersions } from "./CommunicationTemplatesSlice";
import ReplayIcon from "@mui/icons-material/Replay";
import ViewTemplate from "./ViewTemplate";
import {
  getAllCommunicationTemplates,
  revertTemplateVersion,
} from "./CommunicationTemplatesActions";
import CommunicationTemplatesStyles from "./CommunicationTemplatesStyles";
import { useAppDispatch } from "../../store/hooks";

const CommunicationTemplates = () => {
  const dispatch = useAppDispatch();
  const [openCreateTemplateDrawer, setOpenCreateTemplateDrawer] =
    useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [openVersionHistoryDialog, setOpenVersionHistoryDialog] =
    useState<boolean>(false);
  const [openViewTemplateDialog, setOpenViewTemplateDialog] =
    useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ICreatedTemplateData | null>(null);
  const [revertTemplateTo, setRevertTemplateTo] =
    useState<ICreatedTemplateData | null>(null);

  const handleDrawerClose = () => {
    setOpenCreateTemplateDrawer(false);
    setSelectedTemplate(null);
    setIsEdit(false);
    dispatch(getAllCommunicationTemplates());
  };
  const closeVersionHistoryDialog = () => {
    setTemplateVersions(null);
    setOpenVersionHistoryDialog(false);
    setSelectedTemplate(null);
  };

  return (
    <Box 
      sx={{ paddingTop: "2rem" }}
      id="communication_templates_container"
      data-test-id="communication_templates_container"
      aria-label="Communication Templates"
    >
      <Box 
        sx={CommunicationTemplatesStyles.titleContainer}
        id="communication_templates_header"
        data-test-id="communication_templates_header"
        aria-label="Communication Templates Header"
      >
        <Typography
          variant="h6"
          sx={{
            background: (theme: Theme) =>
              `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: "bold",
          }}
          id="communication_templates_title"
          data-test-id="communication_templates_title"
          aria-label="Communication Templates"
        >
          Communication Templates
        </Typography>
        <Button
          startIcon={<AddIcon />}
          sx={{ borderRadius: "4px" }}
          onClick={() => setOpenCreateTemplateDrawer(true)}
          id="create_template_button"
          data-test-id="create_template_button"
          aria-label="Create New Template"
        >
          Create Template
        </Button>
      </Box>
      <CommunicationTemplatesList
        setIsEdit={setIsEdit}
        setOpenCreateTemplateDrawer={setOpenCreateTemplateDrawer}
        setSelectedTemplate={setSelectedTemplate}
        setOpenVersionHistoryDialog={setOpenVersionHistoryDialog}
        setOpenViewTemplateDialog={setOpenViewTemplateDialog}
      />
      <CustomDrawer
        title={isEdit ? "Edit Template" : "Create New Template"}
        open={openCreateTemplateDrawer}
        onClose={handleDrawerClose}
        drawerStyles={{
          zIndex: "1300",
        }}
        headerStyles={{
          "& .MuiTypography-root": {
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
          },
        }}
        content={
          <CreateCommunicationTemplate
            isEdit={isEdit}
            template={selectedTemplate}
            handleDrawerClose={handleDrawerClose}
          />
        }
      />
      {selectedTemplate && (
        <CustomDialog
          open={openVersionHistoryDialog}
          onClose={closeVersionHistoryDialog}
          content={
            <TemplateVersionsHistory
              setRevertTemplateTo={setRevertTemplateTo}
            />
          }
          showDivider
          maxWidth="lg"
          title={`Versions History: ${selectedTemplate?.templateName}`}
        />
      )}
      {selectedTemplate && (
        <CustomDialog
          open={openViewTemplateDialog}
          onClose={() => setOpenViewTemplateDialog(false)}
          content={<ViewTemplate template={selectedTemplate} />}
          showDivider
          maxWidth="md"
          title={selectedTemplate?.templateName}
        />
      )}
    </Box>
  );
};

export default CommunicationTemplates;
